import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { 
  Shield, AlertTriangle, CheckCircle, Clock, FileText, 
  ShoppingCart, RefreshCw, AlertCircle, Upload, ExternalLink
} from 'lucide-react';

const API_URL = getApiBaseUrl();

const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

const statusColors = {
  valid: 'bg-green-100 text-green-800 border-green-200',
  expiring_soon: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  expired: 'bg-red-100 text-red-800 border-red-200',
  missing: 'bg-gray-100 text-gray-800 border-gray-200',
};

const statusIcons = {
  valid: <CheckCircle className="w-5 h-5 text-green-600" />,
  expiring_soon: <Clock className="w-5 h-5 text-yellow-600" />,
  expired: <AlertTriangle className="w-5 h-5 text-red-600" />,
  missing: <AlertCircle className="w-5 h-5 text-gray-500" />,
};

export default function PropertyCompliance({ propertyId, onComplianceChange }) {
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ordering, setOrdering] = useState(null);

  useEffect(() => {
    if (propertyId) {
      fetchComplianceStatus();
    }
  }, [propertyId]);

  const fetchComplianceStatus = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/compliance/properties/${propertyId}/status`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setCompliance(data);
        if (onComplianceChange) {
          onComplianceChange(data);
        }
      }
    } catch (error) {
      console.error('Error fetching compliance:', error);
      toast.error('Failed to load compliance status');
    } finally {
      setLoading(false);
    }
  };

  const orderService = async (certificateType) => {
    setOrdering(certificateType);
    try {
      const response = await fetch(`${API_URL}/api/compliance/service-orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          property_id: propertyId,
          certificate_type: certificateType
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Service order created! Cost: £${data.cost}`);
        fetchComplianceStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create order');
      }
    } catch (error) {
      toast.error('Failed to create service order');
    } finally {
      setOrdering(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          <div className="space-y-2">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-16 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (!compliance) {
    return (
      <Card className="p-6">
        <p className="text-gray-500">Unable to load compliance status</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      {/* Header with overall status */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-lg ${compliance.is_compliant ? 'bg-green-100' : 'bg-red-100'}`}>
            <Shield className={`w-6 h-6 ${compliance.is_compliant ? 'text-green-600' : 'text-red-600'}`} />
          </div>
          <div>
            <h2 className="text-xl font-bold">Property Compliance</h2>
            <p className="text-sm text-gray-600">
              {compliance.jurisdiction.charAt(0).toUpperCase() + compliance.jurisdiction.slice(1)} jurisdiction
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-bold ${compliance.is_compliant ? 'text-green-600' : 'text-red-600'}`}>
            {compliance.compliance_percentage}%
          </div>
          <p className="text-sm text-gray-500">
            {compliance.total_valid}/{compliance.total_required} valid
          </p>
        </div>
      </div>

      {/* Tenancy signing status */}
      {!compliance.can_sign_tenancy && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-red-800 font-medium mb-2">
            <AlertTriangle className="w-5 h-5" />
            Cannot Sign Tenancy
          </div>
          <p className="text-sm text-red-700">
            Complete all mandatory certificates before signing a tenancy agreement.
            {compliance.missing_mandatory.length > 0 && (
              <span className="block mt-1">
                Missing: {compliance.missing_mandatory.map(t => t.replace(/_/g, ' ')).join(', ')}
              </span>
            )}
            {compliance.expired.length > 0 && (
              <span className="block mt-1">
                Expired: {compliance.expired.map(t => t.replace(/_/g, ' ')).join(', ')}
              </span>
            )}
          </p>
        </div>
      )}

      {compliance.can_sign_tenancy && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-green-800 font-medium">
            <CheckCircle className="w-5 h-5" />
            Ready for Tenancy
          </div>
          <p className="text-sm text-green-700">
            All mandatory compliance certificates are valid. You can proceed with tenancy signing.
          </p>
        </div>
      )}

      {/* Certificate list */}
      <div className="space-y-3">
        <h3 className="font-semibold text-gray-700 mb-3">Certificates</h3>
        
        {compliance.compliance_items.map((item) => (
          <div 
            key={item.type}
            className={`border rounded-lg p-4 ${statusColors[item.status]}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                {statusIcons[item.status]}
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold">{item.name}</h4>
                    {item.mandatory && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                        Mandatory
                      </span>
                    )}
                  </div>
                  <p className="text-sm opacity-80 mt-1">{item.reason}</p>
                  
                  {item.status === 'valid' && item.days_until_expiry && (
                    <p className="text-sm mt-1">
                      Expires in {item.days_until_expiry} days ({item.expiry_date})
                    </p>
                  )}
                  
                  {item.status === 'expiring_soon' && (
                    <p className="text-sm font-medium mt-1">
                      ⚠️ Expires in {item.days_until_expiry} days - renew soon!
                    </p>
                  )}
                  
                  {item.status === 'expired' && (
                    <p className="text-sm font-medium mt-1">
                      ❌ Expired {Math.abs(item.days_until_expiry)} days ago
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {item.document_url && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(item.document_url, '_blank')}
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    View
                  </Button>
                )}
                
                {(item.status === 'missing' || item.status === 'expired' || item.status === 'expiring_soon') && !item.order_pending && (
                  <Button
                    size="sm"
                    onClick={() => orderService(item.type)}
                    disabled={ordering === item.type}
                    className="bg-primary hover:bg-primary/90"
                  >
                    {ordering === item.type ? (
                      <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <ShoppingCart className="w-4 h-4 mr-1" />
                    )}
                    Order (£{item.price})
                  </Button>
                )}
                
                {item.order_pending && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                    Order Pending
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pending orders */}
      {compliance.pending_orders > 0 && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 text-blue-800">
            <Clock className="w-5 h-5" />
            <span className="font-medium">{compliance.pending_orders} service order(s) pending</span>
          </div>
          <p className="text-sm text-blue-700 mt-1">
            We&apos;re assigning contractors to your orders. You&apos;ll be notified when scheduled.
          </p>
        </div>
      )}

      {/* Refresh button */}
      <div className="mt-6 flex justify-end">
        <Button variant="outline" size="sm" onClick={fetchComplianceStatus}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Status
        </Button>
      </div>
    </Card>
  );
}
